import { db, type KvRow } from '../db';
import { AbstractBaseRepo } from './baseRepo';

const NOSTRO_RECAPITO_KEY = 'nostroRecapito';
const INSPECTION_DURATION_KEY = 'inspectionDurationHours';
export interface AppSettings {
  nostroRecapito: string;
  inspectionDurationHours: number;
}

export const DEFAULT_SETTINGS: AppSettings = {
  nostroRecapito: 'tecnico@lucamilanesi.it',
  inspectionDurationHours: 2,
};

const parseNumberSetting = (value: unknown, fallback: number): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
};

class SettingsRepoImpl extends AbstractBaseRepo<KvRow, KvRow, KvRow, KvRow, string> {
  protected readonly table = db.kv;

  protected getRowId(row: KvRow): string {
    return row.key;
  }

  protected getInputId(input: KvRow): string | null {
    return input.key ?? null;
  }

  protected toListModel(row: KvRow): KvRow {
    return row;
  }

  protected toGetModel(row: KvRow): KvRow {
    return row;
  }

  protected toRow(input: KvRow): KvRow {
    return input;
  }

  async getSettings(): Promise<AppSettings> {
    const nostroRecapitoRow = await this.table.get(NOSTRO_RECAPITO_KEY);
    const inspectionDurationRow = await this.table.get(INSPECTION_DURATION_KEY);

    return {
      nostroRecapito: (nostroRecapitoRow?.value as string) || DEFAULT_SETTINGS.nostroRecapito,
      inspectionDurationHours: parseNumberSetting(
        inspectionDurationRow?.value,
        DEFAULT_SETTINGS.inspectionDurationHours
      ),
    };
  }

  async updateSettings(settings: Partial<AppSettings>): Promise<void> {
    if (settings.nostroRecapito !== undefined) {
      await this.table.put({
        key: NOSTRO_RECAPITO_KEY,
        value: settings.nostroRecapito,
      });
    }
    if (settings.inspectionDurationHours !== undefined) {
      await this.table.put({
        key: INSPECTION_DURATION_KEY,
        value: settings.inspectionDurationHours,
      });
    }
  }

  async resetSettings(): Promise<void> {
    await Promise.all([
      this.table.delete(NOSTRO_RECAPITO_KEY),
      this.table.delete(INSPECTION_DURATION_KEY),
    ]);
  }

  async clearAllData(): Promise<void> {
    // Clear all tables in the database
    await Promise.all([
      db.companies.clear(),
      db.sites.clear(),
      db.contacts.clear(),
      db.units.clear(),
      db.reports.clear(),
      db.actions.clear(),
      db.schemas.clear(),
      db.templates.clear(),
      db.componentCategories.clear(),
      db.syncOutbox.clear(),
      db.meta.clear(),
      db.media.clear(),
      this.table.clear(),
    ]);
  }
}

export const settingsRepo = new SettingsRepoImpl();
